import map from 'lodash/map'
import merge from 'lodash/merge'
import get from 'lodash/get'
import type { GetOptionLabel, GroupBase, Props, MultiValue } from 'react-select'
import { components } from 'react-select'
import type { FunctionComponent } from 'react'
import React from 'react'
import { useTheme } from '@ztechapps/common.hook'
import type { ApolloClient, FetchPolicy } from '@apollo/client'
import type { AsyncProps } from 'react-select/async'
import type { CreatableProps } from 'react-select/creatable'
import type { FilterQuery, IOption, QueryOptions } from '@ztechapps/common.contract'

export type DataLoadOptions<T = any> = {
  filter?: ({ input }: { input: Record<any, any> }) => FilterQuery<T>
  resultFieldName?: string
  options?: ({ input }: { input?: Record<any, any> }) => QueryOptions<T>
  fetchPolicy?: FetchPolicy
}

export const useThemeColors = (defaultTheme: any) => {
  const { colors } = useTheme()

  if (defaultTheme) {
    return defaultTheme
  }

  let theme = customTheme
  if (colors) {
    theme = (e) => {
      return {
        ...e,
        colors: {
          ...e.colors,
          ...colors,
        },
      }
    }
  }

  return theme
}

export const customTheme = (theme) => {
  return {
    ...theme,
    colors: {
      ...theme.colors,
      primary: '#81352E',
      primary75: '#AE7E7A',
      primary50: '#BA908D',
      primary25: '#C5A3A0',
    },
  }
}

export const setupOptionLabel = (labelKey: string) => (option: SelectOption) => get(option, labelKey)
export const setupLoadOptions =
  ({
    client,
    dataLoadOptions,
    getOptionLabel,
  }: {
    client: ApolloClient<object>
    dataLoadOptions: DataLoadOptions
    getOptionLabel: GetOptionLabel<SelectOption>
  }) =>
  (input, callback) => {
    const dataLoadOptionsFilter = get(dataLoadOptions, 'filter')
    const query = get(dataLoadOptions, 'query')
    const resultFieldName = get(dataLoadOptions, 'resultFieldName')
    const filter = dataLoadOptionsFilter({ input })
    const options: any = merge({}, { limit: 20 }, dataLoadOptions?.options?.({ input }) ?? {})

    const fetchPolicy = dataLoadOptions?.fetchPolicy ?? 'cache-first'

    client.query({ query, variables: { filter, options }, fetchPolicy }).then((results) => {
      let items = get(results, `data.${resultFieldName}`)

      //just remap to value, label
      items = map(items, (e) => ({
        value: e?._id,
        label: getOptionLabel(e),
        ...e,
      }))

      callback(items)
    })
  }

export const OptionComponent = ({ data, ...rest }: any) => {
  //always set the isSelected to false so it wont highlight
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return <components.Option {...{ ...rest, data, isSelected: false }}>{rest?.label}</components.Option>
}

export const mergeStyle = (newStyle, isMaxWidth) => {
  newStyle = newStyle ?? {}
  const defaultStyle = {
    control: (provided, { isDisabled }) => ({
      ...provided,
      backgroundColor: isDisabled ? 'var(--bs-secondary-bg)' : `var(--bs-body-bg)`,
      color: 'var(--bs-body-color)',
      border: 'var(--bs-border-width) solid var(--bs-border-color)',
      borderRadius: 'var(--bs-border-radius)',
      boxShadow: 'none',
      width: '100%',
      transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
      ':hover': {
        borderColor: 'color-mix(in srgb, var(--bs-primary), white 50%)',
      },
    }),

    menu: (provided) => ({
      ...provided,
      zIndex: 400,
      minWidth: '100%',
      width: isMaxWidth ? 'max-content' : '',
      color: 'var(--bs-body-color)',
      backgroundColor: `var(--bs-light-bg-subtle)`,
      marginTop: '0px',
    }),

    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),

    multiValueRemove: (provided) => ({
      ...provided,
      color: 'var(--bs-danger)',
    }),

    option: (provided, { isFocused, isSelected }) => ({
      ...provided,
      color: 'var(--bs-body-color)',
      backgroundColor: isFocused ? 'var(--bs-warning-bg-subtle)' : isSelected ? 'var(--bs-primary-bg-subtle)' : '',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: 'var(--bs-body-color)',
    }),
    input: (provided) => ({
      ...provided,
      color: 'var(--bs-body-color)',
    }),

    indicatorSeparator: (provided) => ({ ...provided, marginTop: '0px', marginBottom: '0px' }),

    singleValue: (provided) => ({
      ...provided,
      color: 'var(--bs-body-color)',
    }),
  }

  return merge({}, defaultStyle, newStyle)
}

export type SelectOption<T = unknown> = IOption & Record<any, any> & T

export type SelectCreatableOption<T = unknown> = SelectOption<T> & {
  __isCreate?: boolean
}

type SelectPropsBasic<Option = unknown, IsMulti extends boolean = false | true, Group extends GroupBase<Option> = GroupBase<Option>> = Props<
  Option,
  IsMulti,
  Group
> & {
  submitting?: boolean
  disabled?: boolean
  isMaxWidth?: boolean
  components?: any
}

export type SelectProps<
  Option = SelectOption,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>
> = SelectPropsBasic<Option, IsMulti, Group> & {
  labelKey?: string
  valueKey?: string
}

export type SelectAsyncProps<
  Option = SelectOption,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>
> = SelectProps<Option, IsMulti, Group> &
  AsyncProps<Option, IsMulti, Group> & {
    labelKey?: string
    dataLoadOptions?: DataLoadOptions
    filter?: FilterQuery<any>
  }

export type SelectCreatableProps<
  Option = SelectCreatableOption,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>
> = SelectProps<Option, IsMulti, Group> &
  CreatableProps<Option, IsMulti, Group> & {
    //use this if you want to get the actual value that was returned from the server operation,
    //onCreateOption will just return the local string value that was typed in in drop-down
    onServerCreateOption?: (value: Option | MultiValue<Option>) => void
  }

export type SelectAsyncCreatableProps<
  Option = SelectCreatableOption,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>
> = SelectAsyncProps<Option, IsMulti, Group> & SelectCreatableProps<Option, IsMulti, Group>

export type SelectFunctionComponents = FunctionComponent<SelectProps | SelectCreatableProps | SelectAsyncProps | SelectAsyncCreatableProps>
export { SelectOptionColumn } from './Select'
